import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Stethoscope,
  Scissors,
  Briefcase,
  ArrowRight,
  User,
  Bot,
  FileText,
  Star,
  ShieldCheck,
} from "lucide-react";
import AudioSampleCard from "@/components/AudioSampleCard";
import Reveal from "@/components/Reveal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const INDUSTRY_TABS = [
  { id: "healthcare", label: "Healthcare & Clinics", testid: "industry-tab-healthcare" },
  { id: "retail", label: "Retail & Salons", testid: "industry-tab-retail" },
  { id: "professional", label: "Professional Offices", testid: "industry-tab-professional" },
];

const healthcareBenefits = [
  "Eliminate hold times for sick or anxious patients calling at peak hours",
  "Book provider-specific slots — dentist, therapist or physician — with the right duration and prep notes",
  "Deliver pre-appointment directions, parking info and preparation instructions automatically",
  "Escalate urgent symptom calls to your on-call staff with a full conversation summary",
];

const comparisonRows = [
  { feature: "Concurrent Calls", desk: "1 at a time", ivr: "A few lines", ai: "Unlimited" },
  { feature: "24/7 Coverage", desk: "Business hours only", ivr: "Yes, but menus only", ai: "Yes, full conversations" },
  { feature: "Response Speed", desk: "Rings + hold time", ivr: "Menu navigation", ai: "Answers on ring 1" },
  { feature: "Direct Calendar Booking", desk: "Manual entry", ivr: "Not possible", ai: "Real-time, rule-based" },
  { feature: "Monthly Cost Profile", desk: "Full-time salary + turnover", ivr: "License + per-minute fees", ai: "Flat, predictable plan" },
];

const leadSheetRows = [
  { label: "Caller", value: "Rohit Sharma — New Client" },
  { label: "Matter", value: "Property dispute consultation" },
  { label: "Urgency", value: "High — court date in 3 weeks" },
  { label: "Preferred Slot", value: "Tue 11:00 AM (tentative hold placed)" },
  { label: "Status", value: "Qualified — forwarded to partner" },
];

const cardMotion = {
  initial: { opacity: 0, y: 44 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function UseCases() {
  const [active, setActive] = useState("healthcare");
  const sectionRefs = {
    healthcare: useRef(null),
    retail: useRef(null),
    professional: useRef(null),
  };

  const goTo = (id) => {
    setActive(id);
    sectionRefs[id].current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="use-cases-page">
      {/* Header + switcher */}
      <section className="mx-auto max-w-7xl px-4 pb-10 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Reveal>
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-indigo-600">Use Cases & Industries</p>
          <h1 className="mx-auto mt-3 max-w-3xl text-center text-4xl font-bold leading-[1.15] tracking-tight text-slate-900 sm:text-5xl">
            Built for Businesses Where Every Phone Call Counts
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="industry-switcher">
            {INDUSTRY_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => goTo(t.id)}
                data-testid={t.testid}
                className={`rounded-full px-6 py-2.5 text-sm font-semibold shadow-sm transition-colors duration-200 ${
                  active === t.id
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Industry 1: Healthcare */}
      <section ref={sectionRefs.healthcare} className="scroll-mt-24 px-4 py-10 sm:px-6 lg:px-8" data-testid="industry-section-healthcare">
        <motion.div {...cardMotion} className="mx-auto max-w-7xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 sm:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50">
                <Stethoscope className="h-6 w-6 text-indigo-600" />
              </span>
              <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Healthcare, Dental & Therapy Clinics
              </h2>
              <ul className="mt-6 space-y-3.5">
                {healthcareBenefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Google Calendar", "Practo API", "HIPAA/DPDP Aware"].map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                    <ShieldCheck className="h-3 w-3 text-indigo-500" />
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to="/contact?industry=clinic"
                data-testid="clinic-demo-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-indigo-700"
              >
                See Clinic Demo
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="border-t border-slate-100 bg-slate-50 p-8 sm:p-10 lg:border-l lg:border-t-0">
              <img
                src="https://images.pexels.com/photos/6809645/pexels-photo-6809645.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Clean and modern dental clinic reception area"
                className="h-40 w-full rounded-xl object-cover"
                loading="lazy"
              />
              <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="clinic-transcript-card">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Rescheduling, handled end-to-end</p>
                <div className="mt-4 flex items-start gap-2.5">
                  <User className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                  <p className="rounded-xl rounded-tl-sm bg-slate-100 px-4 py-2.5 text-sm italic text-slate-700">
                    "I need to move my root canal follow-up from Friday to next week, please."
                  </p>
                </div>
                <div className="mt-3 flex items-start justify-end gap-2.5">
                  <p className="rounded-xl rounded-tr-sm bg-indigo-600 px-4 py-2.5 text-sm italic text-white">
                    "Of course. Dr. Iyer has openings Monday at 10:15 AM or Wednesday at 4:40 PM. Which works better? I'll also re-send your pre-visit instructions."
                  </p>
                  <Bot className="mt-1 h-4 w-4 shrink-0 text-indigo-500" />
                </div>
                <p className="mt-4 flex items-center gap-1.5 text-xs text-emerald-600">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Appointment moved — Practo & Google Calendar updated
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Industry 2: Retail & Salons (inverted) */}
      <section ref={sectionRefs.retail} className="scroll-mt-24 px-4 py-10 sm:px-6 lg:px-8" data-testid="industry-section-retail">
        <motion.div {...cardMotion} className="mx-auto max-w-7xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid lg:grid-cols-2">
            <div className="order-2 border-t border-slate-100 bg-slate-50 p-8 sm:p-10 lg:order-1 lg:border-r lg:border-t-0">
              <img
                src="https://images.pexels.com/photos/7195809/pexels-photo-7195809.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Modern beauty salon reception desk"
                className="h-40 w-full rounded-xl object-cover"
                loading="lazy"
              />
              <div className="mt-5" data-testid="retail-audio-sample">
                <AudioSampleCard title="Auto Repair Service Quote" duration={38} testid="audio-sample-auto-repair" />
              </div>
              <p className="mt-3 text-center text-xs text-slate-400">
                Real scenario: caller gets a brake-pad quote and books a Saturday slot — zero staff involvement.
              </p>
            </div>
            <div className="order-1 p-8 sm:p-10 lg:order-2">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50">
                <Scissors className="h-6 w-6 text-indigo-600" />
              </span>
              <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Retail Stores, Salons & Auto Service Centers
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">
                Your mechanics shouldn't put down their tools and your stylists shouldn't step away
                from a client to quote pricing. CogniTask answers "how much", "are you open" and
                "can I get in today" calls instantly — checking live availability and booking slots
                while your team keeps working.
              </p>
              <ul className="mt-6 space-y-3.5">
                {[
                  "Instant price quotes from your service menu",
                  "Live slot booking for stylists, bays and technicians",
                  "After-hours calls converted into next-day appointments",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                    {b}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact?industry=retail"
                data-testid="retail-workflows-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-indigo-700"
              >
                Explore Retail Workflows
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Industry 3: Professional services */}
      <section ref={sectionRefs.professional} className="scroll-mt-24 px-4 py-10 sm:px-6 lg:px-8" data-testid="industry-section-professional">
        <motion.div {...cardMotion} className="mx-auto max-w-7xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 sm:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50">
                <Briefcase className="h-6 w-6 text-indigo-600" />
              </span>
              <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Professional Services & Corporate Offices
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">
                For legal and financial practices, every inbound call could be a high-value client —
                or a cold caller wasting billable time. CogniTask filters spam and sales calls,
                qualifies prospective client inquiries with your intake questions, and schedules
                discovery calls directly with the right partner.
              </p>
              <ul className="mt-6 space-y-3.5">
                {[
                  "Spam and cold-call filtering before your phone ever rings",
                  "Structured intake: matter type, urgency, budget and timeline",
                  "Discovery calls booked to the correct partner's calendar",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-slate-100 bg-slate-50 p-8 sm:p-10 lg:border-l lg:border-t-0">
              <img
                src="https://images.pexels.com/photos/164586/pexels-photo-164586.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Sleek corporate lobby and reception"
                className="h-40 w-full rounded-xl object-cover"
                loading="lazy"
              />
              <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="lead-sheet-mockup">
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    <FileText className="h-4 w-4 text-indigo-500" />
                    AI Intake Summary — Lead Sheet
                  </p>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">Qualified</span>
                </div>
                <dl className="mt-4 divide-y divide-slate-100">
                  {leadSheetRows.map((r) => (
                    <div key={r.label} className="flex items-center justify-between gap-4 py-2.5">
                      <dt className="text-xs font-medium text-slate-400">{r.label}</dt>
                      <dd className="text-right text-sm font-medium text-slate-800">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Comparison table */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-indigo-600">Head-to-Head</p>
          <h2 className="mx-auto mt-3 max-w-2xl text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Why Businesses Switch to CogniTask
          </h2>
        </Reveal>
        <motion.div
          {...cardMotion}
          className="mt-12 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm"
          data-testid="comparison-table"
        >
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="min-w-[160px] font-semibold text-slate-900">Feature / Capability</TableHead>
                <TableHead className="min-w-[150px] text-slate-600">Traditional Front Desk</TableHead>
                <TableHead className="min-w-[150px] text-slate-600">Legacy "Press 1" IVR</TableHead>
                <TableHead className="min-w-[170px] bg-indigo-600 font-semibold text-white">
                  <span className="flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5 fill-white" />
                    CogniTask AI Voice
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonRows.map((row) => (
                <TableRow key={row.feature}>
                  <TableCell className="font-medium text-slate-900">{row.feature}</TableCell>
                  <TableCell className="text-sm text-slate-600">{row.desk}</TableCell>
                  <TableCell className="text-sm text-slate-600">{row.ivr}</TableCell>
                  <TableCell className="bg-indigo-50/70 text-sm font-semibold text-indigo-700">{row.ai}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
        <div className="mt-12 text-center">
          <Link
            to="/contact"
            data-testid="use-cases-cta-button"
            className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-8 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-indigo-700"
          >
            Book a Demo for Your Industry
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
